import { supabase } from '../../lib/supabase';

/**
 * Error thrown when the stored document changed since it was loaded (another tab or a teammate).
 */
export class DocumentConflictError extends Error {
  /**
   * @param {string|null} serverUpdatedAt - The server's current `updated_at`.
   */
  constructor(serverUpdatedAt) {
    super('This document was changed elsewhere since you opened it.');
    this.name = 'DocumentConflictError';
    this.serverUpdatedAt = serverUpdatedAt;
  }
}

/**
 * Conflict-safe save of a generated document's HTML. The write only applies when the row's
 * `updated_at` still equals `expectedUpdatedAt` (optimistic concurrency); pass `force: true`
 * to overwrite after the user chose to. This is the single DB write for an editor save:
 * opener windows only refresh their lists from the DOCUMENT_UPDATED message.
 *
 * @param {{documentId: string, content: string, expectedUpdatedAt?: string|null, force?: boolean}} input - Save input.
 * @returns {Promise<{updatedAt: string|null}>} The new `updated_at`.
 * @throws {DocumentConflictError} When the row changed since `expectedUpdatedAt`.
 */
export async function saveDocumentContent({ documentId, content, expectedUpdatedAt = null, force = false }) {
  if (!documentId) throw new Error('No document id');
  const updatedAt = new Date().toISOString();
  let query = supabase
    .from('generated_documents')
    .update({ content, updated_at: updatedAt })
    .eq('id', documentId);
  if (!force && expectedUpdatedAt) query = query.eq('updated_at', expectedUpdatedAt);
  const { data, error } = await query.select('id, updated_at');
  if (error) throw error;
  if (Array.isArray(data) && data.length > 0) {
    return { updatedAt: data[0].updated_at || updatedAt };
  }
  // Zero rows: either the document changed elsewhere or it is not writable for this user.
  const { data: current, error: readError } = await supabase
    .from('generated_documents')
    .select('id, updated_at')
    .eq('id', documentId)
    .maybeSingle();
  if (readError) throw readError;
  if (!current) throw new Error('You no longer have access to this document.');
  if (!force && expectedUpdatedAt && current.updated_at !== expectedUpdatedAt) {
    throw new DocumentConflictError(current.updated_at || null);
  }
  throw new Error('The document could not be saved. You may not have permission to edit it.');
}

/**
 * Tell the window that opened this editor (if any) that the document changed, so its lists
 * can refresh. Uses the app's own origin as target (never '*').
 * @param {{documentId: string, updatedAt: string|null, content?: string, title?: string}} data - Message payload.
 * @returns {void}
 */
export function notifyOpenerDocumentUpdated(data) {
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: 'DOCUMENT_UPDATED', data }, window.location.origin);
    }
  } catch {
    /* opener may be cross-origin or gone */
  }
}
